import React from 'react'
import { getBugsnag } from '@/utils/bugsnag.client'

type InlineErrorBoundaryProps = {
  readonly children: React.ReactNode
  readonly label?: string
  readonly retry?: () => void
  readonly resetKeys?: ReadonlyArray<unknown>
  readonly resetOnChildrenChange?: boolean
}

type InlineErrorBoundaryState = {
  hasError: boolean
  error?: Error
}

export class InlineErrorBoundary extends React.Component<
  InlineErrorBoundaryProps,
  InlineErrorBoundaryState
> {
  constructor(props: InlineErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error) {
    const bugsnag = getBugsnag()
    if (bugsnag) {
      bugsnag.notify(error)
    }
  }

  componentDidUpdate(prevProps: InlineErrorBoundaryProps) {
    const shouldResetByChildren =
      (this.props.resetOnChildrenChange ?? true) &&
      prevProps.children !== this.props.children

    const prevKeys = prevProps.resetKeys ?? []
    const nextKeys = this.props.resetKeys ?? []
    const shouldResetByKeys = !InlineErrorBoundary.shallowArrayEqual(
      prevKeys,
      nextKeys,
    )

    if ((this.state.hasError && shouldResetByChildren) || shouldResetByKeys) {
      this.reset()
    }
  }

  private static shallowArrayEqual(
    a: ReadonlyArray<unknown>,
    b: ReadonlyArray<unknown>,
  ) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i += 1) {
      if (!Object.is(a[i], b[i])) return false
    }
    return true
  }

  private reset() {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <div className="flex items-center justify-between">
            <p>{this.props.label ?? 'Une erreur est survenue'}</p>
            {(this.props.retry || true) && (
              <button
                type="button"
                onClick={() => {
                  this.reset()
                  if (this.props.retry) this.props.retry()
                }}
                className="text-xs underline hover:text-red-800"
              >
                Réessayer
              </button>
            )}
          </div>
          {process.env.NODE_ENV === 'development' &&
            this.state.error?.message && (
              <pre className="mt-2 overflow-auto rounded bg-red-100 p-2 text-xs text-red-800">
                {this.state.error.message}
              </pre>
            )}
        </div>
      )
    }
    return this.props.children
  }
}
